import {
  FILE_DELETIONS,
  FILE_SCHEDULES,
  type FilePreview,
  type FileSource,
  IMPORT_TARGETS,
  type ImportTarget,
} from "@kontaktfix/shared";
import { Button, Field, Input, Select, Textarea } from "@kontaktfix/ui";
import { FileArrowDownIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api } from "@/lib/api";
import { toastError } from "@/lib/errors";
import { formatNumber } from "@/lib/i18n";
import { targetLabel } from "@/pages/import";

/** Text for a fetch problem code from the server (issue #28), e.g. "access_denied". */
export function fileReason(t: (key: string, options?: Record<string, unknown>) => string, error: string) {
  const [code, ...rest] = error.split(": ");
  const text = t(`lists.file.reasons.${code}`, { defaultValue: "" });
  return text ? (rest.length ? `${text} (${rest.join(": ")})` : text) : error;
}

/** The source of a file list (issue #28): where the file is, how often, and which column is what. */
export function FileSourceFields({
  value,
  onChange,
  secret,
  onSecretChange,
  hasSecret,
  listId,
}: {
  value: FileSource;
  onChange: (next: FileSource) => void;
  secret: string;
  onSecretChange: (secret: string) => void;
  hasSecret: boolean;
  listId?: string;
}) {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<FilePreview | null>(null);
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  const set = (patch: Partial<FileSource>) => onChange({ ...value, ...patch });
  const sftp = value.url.trim().toLowerCase().startsWith("sftp:");
  const authTypes = sftp ? (["password", "key"] as const) : (["none", "basic", "bearer"] as const);
  const columns = preview?.columns ?? Object.keys(value.mapping);

  const setUrl = (url: string) => {
    const nowSftp = url.trim().toLowerCase().startsWith("sftp:");
    // Keep the sign-in type valid for the protocol.
    const authType = nowSftp
      ? value.authType === "key"
        ? "key"
        : "password"
      : ["basic", "bearer"].includes(value.authType)
        ? value.authType
        : "none";
    set({ url, authType });
  };

  const read = async () => {
    setReading(true);
    setReadError(null);
    try {
      const result = await api.post<FilePreview>("/api/lists/file/preview", {
        file: value,
        secret: secret || undefined,
        listId,
      });
      setPreview(result);
      // Keep what was mapped before for columns that still exist.
      const mapping: Record<string, ImportTarget | null> = {};
      for (const column of result.columns) {
        mapping[column] =
          column in value.mapping
            ? (value.mapping[column] ?? null)
            : (result.suggestedMapping[column] ?? null);
      }
      set({
        mapping,
        keyColumn: value.keyColumn && result.columns.includes(value.keyColumn) ? value.keyColumn : null,
      });
    } catch (err) {
      if (err instanceof ApiError && err.code === "file_fetch_failed") {
        setReadError(fileReason(t, String(err.details.reason ?? "")));
      } else toastError(err);
    } finally {
      setReading(false);
    }
  };

  const secretLabel = {
    none: "",
    basic: t("lists.file.password"),
    bearer: t("lists.file.token"),
    password: t("lists.file.password"),
    key: t("lists.file.privateKey"),
  }[value.authType];

  return (
    <div className="flex flex-col gap-4">
      <Field label={t("lists.file.url")} htmlFor="file-url" hint={t("lists.file.urlHint")}>
        <Input
          id="file-url"
          value={value.url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://export.muster.ch/kontakte.csv"
          required
        />
      </Field>
      <Field label={t("lists.file.auth")} htmlFor="file-auth">
        <Select
          id="file-auth"
          value={value.authType}
          onChange={(e) => set({ authType: e.target.value as FileSource["authType"] })}
        >
          {authTypes.map((a) => (
            <option key={a} value={a}>
              {t(`lists.file.authTypes.${a}`)}
            </option>
          ))}
        </Select>
      </Field>
      {value.authType !== "none" && value.authType !== "bearer" && (
        <Field label={t("lists.file.username")} htmlFor="file-username">
          <Input
            id="file-username"
            value={value.username}
            onChange={(e) => set({ username: e.target.value })}
            autoComplete="off"
          />
        </Field>
      )}
      {value.authType !== "none" && (
        <Field
          label={secretLabel}
          htmlFor="file-secret"
          hint={hasSecret ? t("lists.file.secretStored") : t("lists.file.secretHint")}
        >
          {value.authType === "key" ? (
            <Textarea
              id="file-secret"
              value={secret}
              onChange={(e) => onSecretChange(e.target.value)}
              rows={4}
              className="font-mono text-[11.5px]"
              placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
            />
          ) : (
            <Input
              id="file-secret"
              type="password"
              value={secret}
              onChange={(e) => onSecretChange(e.target.value)}
              autoComplete="new-password"
            />
          )}
        </Field>
      )}

      <div className="flex flex-col gap-4">
        <Field label={t("lists.file.schedule")} htmlFor="file-schedule">
          <Select
            id="file-schedule"
            value={value.schedule}
            onChange={(e) => set({ schedule: e.target.value as FileSource["schedule"] })}
          >
            {FILE_SCHEDULES.map((s) => (
              <option key={s} value={s}>
                {t(`lists.file.schedules.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("lists.file.deletion")} htmlFor="file-deletion">
          <Select
            id="file-deletion"
            value={value.deletion}
            onChange={(e) => set({ deletion: e.target.value as FileSource["deletion"] })}
          >
            {FILE_DELETIONS.map((d) => (
              <option key={d} value={d}>
                {t(`lists.file.deletions.${d}`, { count: value.deletionDays })}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      {value.deletion === "after_days" && (
        <Field label={t("lists.file.deletionDays")} htmlFor="file-days" hint={t("lists.file.deletionHint")}>
          <Input
            id="file-days"
            type="number"
            min={1}
            max={90}
            className="w-24"
            value={value.deletionDays}
            onChange={(e) => set({ deletionDays: Math.min(90, Math.max(1, Number(e.target.value) || 1)) })}
          />
        </Field>
      )}

      <div className="flex flex-col gap-2">
        <div>
          <Button type="button" size="sm" onClick={read} loading={reading} disabled={!value.url.trim()}>
            <FileArrowDownIcon />
            {t("lists.file.read")}
          </Button>
        </div>
        {readError && <p className="text-[12.5px] text-danger">{readError}</p>}
        {preview && (
          <p className="text-[12.5px] text-fg-muted">
            {t("lists.file.readResult", {
              rows: formatNumber(preview.total),
              columns: preview.columns.length,
            })}
          </p>
        )}
        {!preview && !columns.length && (
          <p className="text-[12.5px] text-fg-muted">{t("lists.file.readHint")}</p>
        )}
      </div>

      {columns.length > 0 && (
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-[13px] font-medium">{t("lists.file.mapping")}</legend>
          {columns.map((column) => {
            const index = preview?.columns.indexOf(column) ?? -1;
            const sample = index >= 0 ? preview?.rows.find((r) => r[index])?.[index] : undefined;
            return (
              <div key={column} className="grid items-center gap-2 sm:grid-cols-[1fr_220px]">
                <div className="min-w-0 text-[12.5px]">
                  <div className="truncate font-medium">{column}</div>
                  {sample && <div className="truncate text-fg-muted">{sample}</div>}
                </div>
                <Select
                  aria-label={column}
                  value={value.mapping[column] ?? ""}
                  onChange={(e) =>
                    set({
                      mapping: {
                        ...value.mapping,
                        [column]: (e.target.value || null) as ImportTarget | null,
                      },
                    })
                  }
                >
                  <option value="">{t("import.ignore")}</option>
                  {IMPORT_TARGETS.map((target) => (
                    <option key={target} value={target}>
                      {targetLabel(t, target)}
                    </option>
                  ))}
                </Select>
              </div>
            );
          })}
          <Field label={t("lists.file.keyColumn")} htmlFor="file-key" hint={t("lists.file.keyColumnHint")}>
            <Select
              id="file-key"
              value={value.keyColumn ?? ""}
              onChange={(e) => set({ keyColumn: e.target.value || null })}
            >
              <option value="">{t("lists.file.keyAuto")}</option>
              {columns.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
        </fieldset>
      )}
    </div>
  );
}
